import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { 
  Mail, LogOut, BookOpen, Settings, Camera, Loader2, 
  Wine, Receipt, Wallet, Copy, Clock, Users, Heart, 
  TrendingUp, Calendar, Eye, Star, Send, UserPlus, Trophy,
  EyeOff, Save, CalendarDays, MessageSquare, Lock, MapPin, Target,
  BarChart3, ArrowUp, ArrowDown, Home, Briefcase
} from "lucide-react";
import gustoAvatar from "@/assets/gusto-avatar.png";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ChefAvatar from "@/components/ChefAvatar";
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend
} from "recharts";

const ProfilePage = () => {
  const { user, token, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [myRecipes, setMyRecipes] = useState([]);
  const [reproducedRecipes, setReproducedRecipes] = useState([]);
  const [favoriteRecipes, setFavoriteRecipes] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [wines, setWines] = useState([]);
  const [budgetStats, setBudgetStats] = useState(null);
  const [yearlyStats, setYearlyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReceiptImage, setSelectedReceiptImage] = useState(null);
  const [chefAlias, setChefAlias] = useState("");
  const [editPseudo, setEditPseudo] = useState("");
  const [hideIdentity, setHideIdentity] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [userHousehold, setUserHousehold] = useState(null);
  
  // Settings states
  const [settingsEmail, setSettingsEmail] = useState("");
  const [settingsPostalCode, setSettingsPostalCode] = useState("");
  const [settingsMonthlyBudget, setSettingsMonthlyBudget] = useState("");
  const [settingsProBudget, setSettingsProBudget] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, [token]);

  const fetchAllData = async () => {
    try {
      const [recipesRes, receiptsRes, winesRes, favoritesRes, yearlyRes, householdRes] = await Promise.all([
        axios.get(`${API}/my-recipes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/receipts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/wine/cellar`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/recipe-favorites`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/receipts/stats/yearly`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/household`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);

      // Separate created vs reproduced recipes
      const created = recipesRes.data.filter(r => r.chef_id === user?.id);
      const reproduced = recipesRes.data.filter(r => r.original_recipe_id);
      
      setMyRecipes(created);
      setReproducedRecipes(reproduced);
      setFavoriteRecipes(favoritesRes.data);
      setReceipts(receiptsRes.data);
      setWines(winesRes.data);
      if (yearlyRes.data) setYearlyStats(yearlyRes.data);
      if (householdRes.data) setUserHousehold(householdRes.data);

      // Init privacy settings from user
      if (user) {
        setChefAlias(user.chef_alias || `Chef ${user.pseudo}`);
        setEditPseudo(user.pseudo || "");
        setHideIdentity(user.hide_identity || false);
        // Init settings
        setSettingsEmail(user.email || "");
        setSettingsPostalCode(user.postal_code || "");
        setSettingsMonthlyBudget(user.monthly_budget?.toString() || "500");
        setSettingsProBudget(user.pro_budget?.toString() || "0");
      }

      // Calculate budget stats
      const totalSpent = receiptsRes.data.reduce((sum, r) => sum + (r.total || 0), 0);
      const thisMonth = receiptsRes.data
        .filter(r => new Date(r.date).getMonth() === new Date().getMonth())
        .reduce((sum, r) => sum + (r.total || 0), 0);
      
      setBudgetStats({
        total: totalSpent,
        thisMonth: thisMonth,
        receiptsCount: receiptsRes.data.length,
        averageTicket: receiptsRes.data.length > 0 ? totalSpent / receiptsRes.data.length : 0
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Déconnexion réussie");
    navigate("/");
  };

  const savePrivacySettings = async () => {
    setSavingPrivacy(true);
    try {
      const payload = {
        chef_alias: chefAlias,
        hide_identity: hideIdentity,
      };
      if (editPseudo.trim() && editPseudo.trim() !== user?.pseudo) {
        payload.pseudo = editPseudo.trim();
      }
      await axios.put(`${API}/auth/profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
      await refreshUser();
      toast.success("Profil mis à jour !");
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingPrivacy(false);
    }
  };

  const saveAccountSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {};
      
      // Email update
      if (settingsEmail && settingsEmail !== user?.email) {
        payload.email = settingsEmail;
      }
      
      // Postal code update
      if (settingsPostalCode !== (user?.postal_code || "")) {
        payload.postal_code = settingsPostalCode;
      }
      
      // Monthly budget update
      const budgetValue = parseFloat(settingsMonthlyBudget);
      if (!isNaN(budgetValue) && budgetValue !== user?.monthly_budget) {
        payload.monthly_budget = budgetValue;
      }
      
      // Pro budget update
      const proBudgetValue = parseFloat(settingsProBudget);
      if (!isNaN(proBudgetValue) && proBudgetValue !== user?.pro_budget) {
        payload.pro_budget = proBudgetValue;
      }
      
      // Password update
      if (newPassword) {
        if (newPassword !== confirmPassword) {
          toast.error("Les mots de passe ne correspondent pas");
          setSavingSettings(false);
          return;
        }
        if (newPassword.length < 6) {
          toast.error("Le mot de passe doit faire au moins 6 caractères");
          setSavingSettings(false);
          return;
        }
        if (!currentPassword) {
          toast.error("Veuillez entrer votre mot de passe actuel");
          setSavingSettings(false);
          return;
        }
        payload.current_password = currentPassword;
        payload.new_password = newPassword;
      }
      
      if (Object.keys(payload).length === 0) {
        toast.info("Aucune modification à sauvegarder");
        setSavingSettings(false);
        return;
      }
      
      await axios.put(`${API}/auth/profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
      await refreshUser();
      
      // Clear password fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      
      toast.success("Paramètres mis à jour !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    if (file.size > 15000000) {
      toast.error("Image trop grande (max 15MB)");
      return;
    }

    setUploading(true);

    try {
      // Resize and compress image for avatar (max 400x400, JPEG quality 0.8)
      const compressed = await resizeImage(file, 400, 0.8);
      setAvatarPreview(compressed);

      await axios.put(
        `${API}/auth/profile`,
        { avatar: compressed },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Photo de profil mise à jour !");
      await refreshUser();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUploading(false);
    }
  };

  const resizeImage = (file, maxSize, quality) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let w = img.width, h = img.height;
        if (w > h) { if (w > maxSize) { h = Math.round(h * maxSize / w); w = maxSize; } }
        else { if (h > maxSize) { w = Math.round(w * maxSize / h); h = maxSize; } }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = URL.createObjectURL(file);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header with Avatar */}
      <div className="bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/10 px-4 pt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="relative">
              <button
                onClick={handleAvatarClick}
                className="relative group"
                disabled={uploading}
              >
                <ChefAvatar
                  src={avatarPreview || user?.avatar}
                  name={user?.pseudo}
                  size="xl"
                  showToque={true}
                />
                <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  {uploading ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <Camera className="w-6 h-6 text-white" />
                  )}
                </div>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* User Info */}
            <div className="flex-1">
              <h1 className="font-heading text-2xl font-bold text-charcoal">
                {user?.hide_identity && user?.chef_alias ? user.chef_alias : user?.pseudo}
              </h1>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {user?.email}
              </p>
              <div className="flex gap-2 mt-2">
                <Badge className="bg-primary/10 text-primary">
                  <img src={gustoAvatar} alt="" className="w-3 h-3 mr-1 inline-block" />
                  Chef
                </Badge>
                {user?.total_points > 0 && (
                  <Link to="/points">
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors cursor-pointer">
                      <Trophy className="w-3 h-3 mr-1" />
                      {user.total_points} pts
                    </Badge>
                  </Link>
                )}
                {user?.is_admin && (
                  <Badge className="bg-purple-500">Admin</Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Tabs */}
      <div className="px-4 -mt-4 max-w-4xl mx-auto">
        <Tabs defaultValue="budget" className="w-full">
          <TabsList className="grid w-full grid-cols-7 mb-4 h-auto p-1 bg-lavender/20">
            <TabsTrigger value="budget" className="text-xs py-2 px-1">
              <Wallet className="w-4 h-4 mr-1 hidden sm:inline" />
              Budget
            </TabsTrigger>
            <TabsTrigger value="favorites" className="text-xs py-2 px-1" data-testid="tab-favorites">
              <Heart className="w-4 h-4 mr-1 hidden sm:inline" />
              Favoris
            </TabsTrigger>
            <TabsTrigger value="created" className="text-xs py-2 px-1">
              <img src={gustoAvatar} alt="" className="w-4 h-4 mr-1 hidden sm:inline" />
              Créées
            </TabsTrigger>
            <TabsTrigger value="receipts" className="text-xs py-2 px-1">
              <Receipt className="w-4 h-4 mr-1 hidden sm:inline" />
              Tickets
            </TabsTrigger>
            <TabsTrigger value="reproduced" className="text-xs py-2 px-1">
              <Copy className="w-4 h-4 mr-1 hidden sm:inline" />
              Reproduites
            </TabsTrigger>
            <TabsTrigger value="cave" className="text-xs py-2 px-1">
              <Wine className="w-4 h-4 mr-1 hidden sm:inline" />
              Cave
            </TabsTrigger>
            <TabsTrigger value="settings" className="text-xs py-2 px-1" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-1 hidden sm:inline" />
              Réglages
            </TabsTrigger>
          </TabsList>

          {/* Budget Tab */}
          <TabsContent value="budget">
            <div className="space-y-4">
              {/* Budget Personnel */}
              <Card className="shadow-lg">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Wallet className="w-5 h-5 text-green-600" />
                    Budget Personnel (Courses)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl bg-green-50">
                      <p className="text-xs text-muted-foreground">Ce mois</p>
                      <p className="text-xl font-bold text-green-600">
                        {budgetStats?.thisMonth?.toFixed(0) || 0}€
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-blue-50">
                      <p className="text-xs text-muted-foreground">Total année</p>
                      <p className="text-xl font-bold text-blue-600">
                        {budgetStats?.total?.toFixed(0) || 0}€
                      </p>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-medium">Objectif mensuel</span>
                      <span className="text-xs text-muted-foreground">
                        {budgetStats?.thisMonth?.toFixed(0) || 0}€ / {user?.monthly_budget || 500}€
                      </span>
                    </div>
                    <Progress 
                      value={Math.min((budgetStats?.thisMonth || 0) / (user?.monthly_budget || 500) * 100, 100)} 
                      className="h-2.5"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Budget Pro */}
              <Card className="shadow-lg">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Briefcase className="w-5 h-5 text-blue-600" />
                    Budget Pro (Repas professionnels)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl bg-blue-50">
                      <p className="text-xs text-muted-foreground">Ce mois</p>
                      <p className="text-xl font-bold text-blue-600">
                        {yearlyStats?.monthly_data?.[11]?.pro?.toFixed(0) || 0}€
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-indigo-50">
                      <p className="text-xs text-muted-foreground">Total année</p>
                      <p className="text-xl font-bold text-indigo-600">
                        {yearlyStats?.monthly_data?.reduce((s, m) => s + (m.pro || 0), 0)?.toFixed(0) || 0}€
                      </p>
                    </div>
                  </div>
                  {(user?.pro_budget || 0) > 0 && (
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-medium">Budget pro mensuel</span>
                        <span className="text-xs text-muted-foreground">
                          {yearlyStats?.monthly_data?.[11]?.pro?.toFixed(0) || 0}€ / {user?.pro_budget || 0}€
                        </span>
                      </div>
                      <Progress 
                        value={Math.min((yearlyStats?.monthly_data?.[11]?.pro || 0) / (user?.pro_budget || 1) * 100, 100)} 
                        className="h-2.5"
                      />
                    </div>
                  )}
                  {(!user?.pro_budget || user.pro_budget === 0) && (
                    <p className="text-xs text-muted-foreground">
                      Définissez votre budget pro dans les Réglages pour suivre vos dépenses professionnelles.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Budget Foyer */}
              {userHousehold && (
                <Card className="shadow-lg">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Home className="w-5 h-5 text-emerald-600" />
                      Budget Foyer — {userHousehold.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl bg-emerald-50">
                        <p className="text-xs text-muted-foreground">Ce mois</p>
                        <p className="text-xl font-bold text-emerald-600">
                          {userHousehold.stats?.this_month_spent?.toFixed(0) || 0}€
                        </p>
                      </div>
                      <div className="p-3 rounded-xl bg-teal-50">
                        <p className="text-xs text-muted-foreground">Budget total</p>
                        <p className="text-xl font-bold text-teal-600">
                          {userHousehold.monthly_budget || 0}€
                        </p>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-medium">Objectif foyer</span>
                        <span className="text-xs text-muted-foreground">
                          {userHousehold.stats?.this_month_spent?.toFixed(0) || 0}€ / {userHousehold.monthly_budget || 0}€
                        </span>
                      </div>
                      <Progress 
                        value={Math.min((userHousehold.stats?.this_month_spent || 0) / (userHousehold.monthly_budget || 1) * 100, 100)} 
                        className="h-2.5"
                      />
                    </div>
                    <Link to="/household">
                      <Button variant="outline" size="sm" className="w-full gap-2 mt-1">
                        <Home className="w-4 h-4" /> Voir le détail du foyer
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}

              {/* Stats Summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-amber-50 text-center">
                  <p className="text-xs text-amber-600 font-medium">Tickets scannés</p>
                  <p className="text-lg font-bold text-amber-700">{budgetStats?.receiptsCount || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-purple-50 text-center">
                  <p className="text-xs text-purple-600 font-medium">Panier moyen</p>
                  <p className="text-lg font-bold text-purple-700">{budgetStats?.averageTicket?.toFixed(0) || 0}€</p>
                </div>
              </div>

              {/* Yearly Budget Chart */}
              {yearlyStats && yearlyStats.monthly_data && (
                <Card className="shadow-lg">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-charcoal flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-primary" />
                        Évolution annuelle
                      </h3>
                      <Badge variant="outline" className="text-xs">12 derniers mois</Badge>
                    </div>

                    <div 
                      className="h-56 w-full" 
                      tabIndex={-1}
                      style={{ outline: 'none', WebkitTapHighlightColor: 'transparent', userSelect: 'none' }}
                      onFocus={(e) => e.target.blur()}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={yearlyStats.monthly_data}
                          margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
                          style={{ outline: 'none', cursor: 'default' }}
                        >
                          <defs>
                            <linearGradient id="colorPersonal" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorPro" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorHousehold" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="label" 
                            tick={{ fontSize: 10, fill: '#888' }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis 
                            tick={{ fontSize: 10, fill: '#888' }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v) => `${v}€`}
                          />
                          <Tooltip 
                            formatter={(value, name) => {
                              const labels = { personal: 'Perso', pro: 'Pro', household: 'Foyer', total: 'Total' };
                              return [`${Number(value).toFixed(2)}€`, labels[name] || name];
                            }}
                            labelFormatter={(label) => `Mois: ${label}`}
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              borderRadius: '12px',
                              border: '1px solid #eee',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Legend 
                            formatter={(value) => {
                              const labels = { personal: 'Perso', pro: 'Pro', household: 'Foyer' };
                              return labels[value] || value;
                            }}
                            iconType="circle"
                            wrapperStyle={{ fontSize: '11px' }}
                          />
                          {user?.monthly_budget && (
                            <ReferenceLine 
                              y={user.monthly_budget} 
                              stroke="#EF4444" 
                              strokeDasharray="5 5"
                              label={{ 
                                value: `Obj: ${user.monthly_budget}€`, 
                                fontSize: 9, 
                                fill: '#EF4444',
                                position: 'right'
                              }}
                            />
                          )}
                          <Area
                            type="monotone"
                            dataKey="personal"
                            stroke="#22C55E"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorPersonal)"
                            animationDuration={1500}
                            stackId="1"
                          />
                          <Area
                            type="monotone"
                            dataKey="pro"
                            stroke="#3B82F6"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorPro)"
                            animationDuration={1500}
                            stackId="1"
                          />
                          <Area
                            type="monotone"
                            dataKey="household"
                            stroke="#10B981"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorHousehold)"
                            animationDuration={1500}
                            stackId="1"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Stats Summary */}
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      <div className="p-3 rounded-xl bg-violet-50 text-center">
                        <p className="text-xs text-violet-600 font-medium">Total année</p>
                        <p className="text-lg font-bold text-violet-700">{yearlyStats.total_year?.toFixed(0)}€</p>
                      </div>
                      <div className="p-3 rounded-xl bg-blue-50 text-center">
                        <p className="text-xs text-blue-600 font-medium">Moyenne/mois</p>
                        <p className="text-lg font-bold text-blue-700">{yearlyStats.average_monthly?.toFixed(0)}€</p>
                      </div>
                      <div className="p-3 rounded-xl bg-amber-50 text-center">
                        <p className="text-xs text-amber-600 font-medium">Mois actifs</p>
                        <p className="text-lg font-bold text-amber-700">{yearlyStats.months_with_data || 0}</p>
                      </div>
                    </div>

                    {(yearlyStats.max_month || yearlyStats.min_month) && (
                      <div className="flex gap-3 mt-3">
                        {yearlyStats.max_month && yearlyStats.max_month.total > 0 && (
                          <div className="flex-1 p-2 rounded-lg bg-red-50 flex items-center gap-2">
                            <ArrowUp className="w-4 h-4 text-red-500" />
                            <div>
                              <p className="text-xs text-red-600 font-medium">Plus dépensier</p>
                              <p className="text-sm font-semibold text-red-700">{yearlyStats.max_month.label}: {yearlyStats.max_month.total}€</p>
                            </div>
                          </div>
                        )}
                        {yearlyStats.min_month && yearlyStats.min_month.total > 0 && (
                          <div className="flex-1 p-2 rounded-lg bg-green-50 flex items-center gap-2">
                            <ArrowDown className="w-4 h-4 text-green-500" />
                            <div>
                              <p className="text-xs text-green-600 font-medium">Plus économe</p>
                              <p className="text-sm font-semibold text-green-700">{yearlyStats.min_month.label}: {yearlyStats.min_month.total}€</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <Button className="w-full" onClick={() => navigate("/purchase-stats")}>
                <TrendingUp className="w-4 h-4 mr-2" />
                Voir les statistiques détaillées
              </Button>
            </div>
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  Mes recettes favorites
                  <Badge variant="outline">{favoriteRecipes.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {favoriteRecipes.length > 0 ? (
                  <div className="space-y-3">
                    {favoriteRecipes.map((fav, index) => (
                      <div 
                        key={fav.id || index}
                        className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                        data-testid={`favorite-recipe-${index}`}
                      >
                        <Link to={`/recipe/${fav.recipe_id}`} className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500/20 to-pink-500/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {fav.recipe?.final_image ? (
                              <img 
                                src={fav.recipe.final_image.startsWith('data:') ? fav.recipe.final_image : `data:image/jpeg;base64,${fav.recipe.final_image}`}
                                alt={fav.recipe_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Heart className="w-6 h-6 text-red-400/50" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-charcoal truncate">{fav.recipe_name || fav.recipe?.name || "Recette"}</p>
                            <p className="text-xs text-muted-foreground">
                              {fav.recipe?.chef_pseudo && `Par ${fav.recipe.chef_pseudo} · `}
                              Ajouté le {new Date(fav.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </Link>
                        <button
                          onClick={async () => {
                            try {
                              await axios.delete(`${API}/recipe-favorites/${fav.recipe_id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setFavoriteRecipes(prev => prev.filter(f => f.id !== fav.id));
                              toast.success("Retiré des favoris");
                            } catch {
                              toast.error("Erreur");
                            }
                          }}
                          className="p-2 rounded-full hover:bg-red-50 transition-colors flex-shrink-0"
                          title="Retirer des favoris"
                          data-testid={`remove-favorite-${index}`}
                        >
                          <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Heart className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">Aucune recette en favoris</p>
                    <p className="text-xs text-muted-foreground mt-1">Mettez une recette en favoris depuis un repas terminé</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Receipts Tab */}
          <TabsContent value="receipts">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-amber-600" />
                  Tous mes tickets de caisse
                  <Badge variant="outline">{receipts.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {receipts.length > 0 ? (
                  <div className="space-y-3">
                    {receipts.slice(0, 10).map((receipt, index) => (
                      <div 
                        key={receipt.id || index}
                        className="flex items-center justify-between p-3 rounded-xl bg-gray-50"
                        data-testid={`receipt-item-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          {receipt.image_base64 ? (
                            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border border-gray-200">
                              <img
                                src={receipt.image_base64.startsWith('data:') ? receipt.image_base64 : `data:image/jpeg;base64,${receipt.image_base64}`}
                                alt="Ticket"
                                className="w-full h-full object-cover cursor-pointer"
                                onClick={() => setSelectedReceiptImage(receipt.image_base64)}
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                              <Receipt className="w-5 h-5 text-amber-600" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-charcoal">
                              {receipt.store_name || "Magasin"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(receipt.date || receipt.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-charcoal">{receipt.total?.toFixed(2) || 0}€</p>
                          <p className="text-xs text-muted-foreground">
                            {receipt.products?.length || 0} articles
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Receipt className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">Aucun ticket scanné</p>
                    <Link to="/my-receipts">
                      <Button className="mt-4">Scanner un ticket</Button>
                    </Link>
                  </div>
                )}

                {receipts.length > 0 && (
                  <Link to="/my-receipts" className="block mt-4">
                    <Button variant="outline" className="w-full">
                      Voir tous les tickets
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>

            {/* Receipt Image Fullscreen Modal */}
            {selectedReceiptImage && (
              <div
                className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                onClick={() => setSelectedReceiptImage(null)}
              >
                <div className="relative max-w-lg w-full max-h-[85vh]">
                  <button
                    onClick={() => setSelectedReceiptImage(null)}
                    className="absolute -top-10 right-0 text-white hover:text-gray-300"
                  >
                    <Eye className="w-5 h-5 mr-1 inline" /> Fermer
                  </button>
                  <img
                    src={selectedReceiptImage.startsWith('data:') ? selectedReceiptImage : `data:image/jpeg;base64,${selectedReceiptImage}`}
                    alt="Ticket de caisse"
                    className="w-full h-auto max-h-[80vh] object-contain rounded-xl"
                  />
                </div>
              </div>
            )}
          </TabsContent>

          {/* Created Recipes Tab */}
          <TabsContent value="created">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <img src={gustoAvatar} alt="" className="w-5 h-5" />
                  Mes recettes créées
                  <Badge variant="outline">{myRecipes.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {myRecipes.length > 0 ? (
                  <div className="space-y-3">
                    {myRecipes.map((recipe, index) => (
                      <Link key={recipe.id || index} to={`/recipe/${recipe.id}`}>
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center overflow-hidden">
                            {recipe.final_image ? (
                              <img 
                                src={recipe.final_image.startsWith('data:') ? recipe.final_image : `data:image/jpeg;base64,${recipe.final_image}`}
                                alt={recipe.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <img src={gustoAvatar} alt="" className="w-6 h-6 opacity-40" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-charcoal">{recipe.name}</p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Copy className="w-3 h-3" />
                                {recipe.reproductions_count || 0} repros
                              </span>
                              <span className="flex items-center gap-1">
                                <Heart className="w-3 h-3" />
                                {recipe.likes_count || 0}
                              </span>
                            </div>
                          </div>
                          <Badge className="bg-green-100 text-green-700">Créateur</Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <img src={gustoAvatar} alt="" className="h-10 mx-auto opacity-30 mb-3" />
                    <p className="text-muted-foreground">Aucune recette créée</p>
                    <Link to="/record">
                      <Button className="mt-4">Créer ma première recette</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reproduced Recipes Tab */}
          <TabsContent value="reproduced">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Copy className="w-5 h-5 text-blue-600" />
                  Recettes reproduites
                  <Badge variant="outline">{reproducedRecipes.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reproducedRecipes.length > 0 ? (
                  <div className="space-y-3">
                    {reproducedRecipes.map((recipe, index) => (
                      <Link key={recipe.id || index} to={`/recipe/${recipe.id}`}>
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center overflow-hidden">
                            {recipe.final_image ? (
                              <img 
                                src={recipe.final_image.startsWith('data:') ? recipe.final_image : `data:image/jpeg;base64,${recipe.final_image}`}
                                alt={recipe.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Copy className="w-6 h-6 text-blue-500/40" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-charcoal">{recipe.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Recette originale de {recipe.original_chef_pseudo || "Chef"}
                            </p>
                          </div>
                          <Badge className="bg-blue-100 text-blue-700">Reproduite</Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Copy className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">Aucune recette reproduite</p>
                    <Link to="/discover">
                      <Button className="mt-4">Découvrir des recettes</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Wine Cellar Tab */}
          <TabsContent value="cave">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wine className="w-5 h-5 text-red-600" />
                  Ma cave à vins
                  <Badge variant="outline">{wines.length} bouteilles</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {wines.length > 0 ? (
                  <div className="space-y-3">
                    {wines.slice(0, 8).map((wine, index) => (
                      <div 
                        key={wine.id || index}
                        className="flex items-center gap-3 p-3 rounded-xl bg-gray-50"
                      >
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500/20 to-rose-500/20 flex items-center justify-center">
                          <Wine className="w-6 h-6 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-charcoal">{wine.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {wine.year} • {wine.region || wine.type}
                          </p>
                        </div>
                        <Badge variant="outline">
                          x{wine.quantity || 1}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Wine className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">Aucune bouteille dans votre cave</p>
                    <Link to="/wine-cellar">
                      <Button className="mt-4">Ajouter une bouteille</Button>
                    </Link>
                  </div>
                )}

                {wines.length > 0 && (
                  <Link to="/wine-cellar" className="block mt-4">
                    <Button variant="outline" className="w-full">
                      Gérer ma cave
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-gray-600" />
                  Paramètres du compte
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="settings-email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    Adresse email
                  </Label>
                  <Input
                    id="settings-email"
                    type="email"
                    value={settingsEmail}
                    onChange={(e) => setSettingsEmail(e.target.value)}
                    placeholder="votre@email.com"
                    data-testid="settings-email"
                  />
                </div>

                {/* Postal Code */}
                <div className="space-y-2">
                  <Label htmlFor="settings-postal" className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    Code postal
                  </Label>
                  <Input
                    id="settings-postal"
                    type="text"
                    value={settingsPostalCode}
                    onChange={(e) => setSettingsPostalCode(e.target.value)}
                    placeholder="75001"
                    maxLength={5}
                    data-testid="settings-postal"
                  />
                  <p className="text-xs text-muted-foreground">Permet de trouver les commerces près de chez vous</p>
                </div>

                {/* Monthly Budget */}
                <div className="space-y-2">
                  <Label htmlFor="settings-budget" className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-green-600" />
                    Budget courses mensuel (€)
                  </Label>
                  <Input
                    id="settings-budget"
                    type="number"
                    min="0"
                    step="10"
                    value={settingsMonthlyBudget}
                    onChange={(e) => setSettingsMonthlyBudget(e.target.value)}
                    placeholder="500"
                    data-testid="settings-budget"
                  />
                  <p className="text-xs text-muted-foreground">Objectif mensuel pour vos courses personnelles</p>
                </div>

                {/* Pro Budget */}
                <div className="space-y-2">
                  <Label htmlFor="settings-pro-budget" className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-blue-600" />
                    Budget repas pro mensuel (€)
                  </Label>
                  <Input
                    id="settings-pro-budget"
                    type="number"
                    min="0"
                    step="10"
                    value={settingsProBudget}
                    onChange={(e) => setSettingsProBudget(e.target.value)}
                    placeholder="150"
                    data-testid="settings-pro-budget"
                  />
                  <p className="text-xs text-muted-foreground">Budget pour vos repas professionnels (tickets restaurant, etc.)</p>
                </div>

                {/* Foyer Budget Info */}
                {userHousehold && (
                  <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 space-y-2">
                    <div className="flex items-center gap-2">
                      <Home className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-800">Budget Foyer — {userHousehold.name}</span>
                    </div>
                    <p className="text-sm text-emerald-700">
                      Budget total : <strong>{userHousehold.monthly_budget || 0}€/mois</strong>
                    </p>
                    {userHousehold.members?.find(m => m.user_id === user?.id)?.role === "admin" ? (
                      <Link to="/household">
                        <Button variant="outline" size="sm" className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-100">
                          <Settings className="w-3 h-3" /> Gérer les sous-budgets du foyer
                        </Button>
                      </Link>
                    ) : (
                      <p className="text-xs text-emerald-600">Seul l'admin du foyer peut modifier les budgets.</p>
                    )}
                  </div>
                )}

                <Separator />

                {/* Password Change */}
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Lock className="w-4 h-4 text-muted-foreground" />
                    Modifier le mot de passe
                  </h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="current-password">Mot de passe actuel</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="••••••••"
                      data-testid="current-password"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Nouveau mot de passe</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      data-testid="new-password"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      data-testid="confirm-password"
                    />
                  </div>
                </div>

                <Button 
                  onClick={saveAccountSettings}
                  disabled={savingSettings}
                  className="w-full"
                  data-testid="save-settings-btn"
                >
                  {savingSettings ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Sauvegarder les paramètres
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Quick Links */}
        <div className="grid grid-cols-4 gap-3 mt-6">
          <Link to="/household">
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4 text-center">
                <Home className="w-6 h-6 mx-auto text-emerald-500 mb-1" />
                <p className="text-xs font-medium">Foyer</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/friends">
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 mx-auto text-blue-500 mb-1" />
                <p className="text-xs font-medium">Amis</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/my-meals">
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4 text-center">
                <CalendarDays className="w-6 h-6 mx-auto text-violet-500 mb-1" />
                <p className="text-xs font-medium">Mes repas</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/my-ratings">
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4 text-center">
                <MessageSquare className="w-6 h-6 mx-auto text-amber-500 mb-1" />
                <p className="text-xs font-medium">Mes notes</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/points">
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4 text-center">
                <Trophy className="w-6 h-6 mx-auto text-amber-500 mb-1" />
                <p className="text-xs font-medium">{user?.total_points || 0} pts</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/leaderboard">
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4 text-center">
                <Star className="w-6 h-6 mx-auto text-yellow-500 mb-1" />
                <p className="text-xs font-medium">Classement</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/dietary-profile">
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4 text-center">
                <Heart className="w-6 h-6 mx-auto text-red-400 mb-1" />
                <p className="text-xs font-medium">Mes goûts</p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Chef Identity & Privacy */}
        <Card className="mt-4 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <EyeOff className="w-4 h-4" /> Identité & Confidentialité
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-charcoal">Votre pseudo</label>
              <p className="text-xs text-muted-foreground">Ce nom apparaît quand vous ne masquez pas votre identité</p>
              <input
                type="text"
                value={editPseudo}
                onChange={(e) => setEditPseudo(e.target.value)}
                placeholder="Ex: Jey"
                className="w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="edit-pseudo-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-charcoal">Votre alias de Chef</label>
              <p className="text-xs text-muted-foreground">Ce nom sera affiché publiquement quand vous masquez votre identité</p>
              <input
                type="text"
                value={chefAlias}
                onChange={(e) => setChefAlias(e.target.value)}
                placeholder="Ex: Chef Marco"
                className="w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="chef-alias-input"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
              <div>
                <p className="text-sm font-medium text-charcoal">Masquer mon identité</p>
                <p className="text-xs text-muted-foreground">
                  {hideIdentity ? "Vous apparaissez comme : " : "Visible avec votre pseudo : "}
                  <strong>{hideIdentity ? chefAlias || "Chef Anonyme" : user?.pseudo}</strong>
                </p>
              </div>
              <button
                onClick={() => setHideIdentity(!hideIdentity)}
                className={`w-12 h-7 rounded-full transition-colors relative ${hideIdentity ? "bg-primary" : "bg-gray-300"}`}
                data-testid="hide-identity-toggle"
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow absolute top-1 transition-transform ${hideIdentity ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
            <Button
              onClick={savePrivacySettings}
              disabled={savingPrivacy}
              variant="outline"
              className="w-full rounded-full gap-2"
              data-testid="save-privacy-btn"
            >
              {savingPrivacy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </Button>
          </CardContent>
        </Card>

        {/* Settings & Logout */}
        <Card className="mt-4 shadow-lg">
          <CardContent className="p-4 space-y-3">
            <Button variant="ghost" className="w-full justify-start" onClick={() => navigate("/dietary-profile")}>
              <Settings className="w-5 h-5 mr-3" />
              Préférences alimentaires
            </Button>
            {user?.is_admin && (
              <>
                <Separator />
                <Button variant="ghost" className="w-full justify-start text-purple-600 hover:text-purple-700 hover:bg-purple-50" onClick={() => navigate("/admin/dashboard")}>
                  <Settings className="w-5 h-5 mr-3" />
                  Administration
                </Button>
              </>
            )}
            <Separator />
            <Button 
              variant="ghost" 
              className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5 mr-3" />
              Déconnexion
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfilePage;
